import { useState } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useGetAgencyAddons, useUpdateAgencyAddon } from '@/apis/agency/addons/hook';
import { AgencyAddon } from '@/apis/agency/addons/types';
import AddonCard from './AddonCard';
import AddonEditDrawer from './AddonEditDrawer';
import { sortAddonsForDisplay } from './addonDisplay';

export default function AddonPricing() {
  const { data, isLoading } = useGetAgencyAddons();
  const { mutate: updateAddon, isPending: isUpdating, variables } = useUpdateAgencyAddon();
  const [editingAddon, setEditingAddon] = useState<AgencyAddon | null>(null);

  const orderedAddons = sortAddonsForDisplay(data?.data ?? []);

  const handleToggle = (addon: AgencyAddon, active: boolean) => {
    updateAddon({
      id: addon.id,
      payload: { addon: { active } }
    });
  };

  return (
    <Box mt={4} width='100%'>
      <Typography variant='h6'>Add-on Pricing</Typography>
      <Typography color='text.secondary' fontSize={14} mb={2.5} maxWidth={720}>
        Each add-on has its own configuration. Set the pricing and terms that fit your agency — only enabled add-ons are
        offered to your businesses.
      </Typography>

      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid key={index} size={12}>
              <Skeleton variant='rounded' height={160} />
            </Grid>
          ))}
        </Grid>
      ) : orderedAddons.length === 0 ? (
        <Stack alignItems='center' justifyContent='center' py={4} px={2} border='1px dashed #E0E0E0' borderRadius={2}>
          <Typography color='text.secondary'>No add-ons are available for this agency yet.</Typography>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {orderedAddons.map((addon) => (
            <Grid key={addon.id} size={12}>
              <AddonCard
                addon={addon}
                isToggling={isUpdating && String(variables?.id) === String(addon.id)}
                onToggle={(active) => handleToggle(addon, active)}
                onEdit={() => setEditingAddon(addon)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AddonEditDrawer addon={editingAddon} open={Boolean(editingAddon)} onClose={() => setEditingAddon(null)} />
    </Box>
  );
}
